import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useResetActionStepMutation } from '../../../FinancialPlanOPL/Goals/components/Tasks/api/useResetActionStepMutation';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { Lozenge } from '../../../CommonComponents/OPL/Lozenge/Lozenge';
import { AddEditActionStep } from '../../../FinancialPlanOPL/Goals/components/Tasks/components/AddEditActionStep/AddEditActionStep';
import { TaskItem } from '../../../FinancialPlanOPL/Goals/components/Tasks/components/TaskItem/TaskItem';
import { PlanActionStepDTO } from '../../../FinancialPlanOPL/types/planActionStepsDTO';
import {
  GoalTypeDTO,
  PulseCheckCategory as PulseCheckCategoryEnum,
} from '../../../FinancialPlanOPL/types/goalsDTO';
import { isAdviserArea as selectIsAdviserArea } from '../../../CommonSelectors/CommonAreaSelector';
import { RawButton } from '../../../CommonComponents/OPL/Button/Button.styles';
import { useClientId } from '../../../_hooks/useClientId';
import { navigateToGoal } from '../../../FinancialPlanOPL/Goals/goals/Retirement/pages/RetirementSideBySide/utils/navigateToGoal';
import { PlanActionStepsAdvisersAssigneesDTO } from '../../../FinancialPlanOPL/types/planActionStepsAdvisersAssigneesDTO';

interface Props {
  goalName: string;
  goalId?: string;
  actionSteps: PlanActionStepDTO[];
  isOther?: boolean;
  debtType?: number;
  goalType?: GoalTypeDTO;
  pulseCheckCategory?: PulseCheckCategoryEnum;
  isPrint?: boolean;
  linkToGoal?: boolean;
  advisorAssignees: PlanActionStepsAdvisersAssigneesDTO | null;
  showAssetBucketing: boolean;
  availableAssetBuckets: { Title: string }[];
}

export const GoalSection = ({
  goalName,
  goalType,
  actionSteps,
  isOther,
  debtType,
  pulseCheckCategory,
  goalId,
  isPrint,
  linkToGoal,
  advisorAssignees,
  showAssetBucketing,
  availableAssetBuckets,
}: Props) => {
  const clientId = useClientId();

  const [showAddForm, setShowAddForm] = useState(false);

  const isAdvisor = useSelector(selectIsAdviserArea);

  const isActionNeeded = actionSteps.some(({ IsComplete }) => !IsComplete);

  const { mutateAsync: resetTaskMutation, isLoading: resetIsLoading } = useResetActionStepMutation({
    isReport: true,
    goalId,
  });

  const Title = (
    <Text fontWeight={600} fontSize="h6">
      {goalName}
    </Text>
  );

  return (
    <Box borderBottom="1px solid" borderColor="neutral.200" p="16" css="page-break-inside: avoid">
      <Box display="flex" alignItems="center" mb="12" css="gap: 8px">
        {linkToGoal && goalId && goalType ? (
          <RawButton onClick={() => navigateToGoal(clientId.toString(), goalId, goalType)}>
            {Title}
          </RawButton>
        ) : (
          <>{Title}</>
        )}

        {!isPrint && isAdvisor && (
          <Button
            leftIcon="fal fa-plus-circle"
            variant="link"
            onClick={() => setShowAddForm(true)}
            aria-label="Add action step"
            disabled={showAddForm}
          >
            {isOther ? 'Add Item' : 'Action Step'}
          </Button>
        )}

        {!isOther && !isPrint && isAdvisor && (
          <IconButton
            icon="far fa-sync"
            variant="subtleInactive"
            size="small"
            onClick={() => resetTaskMutation()}
            isLoading={resetIsLoading}
            aria-label="Reset action steps"
            ml="4"
          />
        )}

        {isActionNeeded && !isPrint && (
          <Lozenge variant="default" variantColor="warning" ml="auto">
            Action Needed
          </Lozenge>
        )}
      </Box>

      {showAddForm ? (
        <AddEditActionStep
          isReport
          goalId={goalId}
          setFormVisibility={setShowAddForm}
          debtType={debtType}
          pulseCheckCategory={pulseCheckCategory}
          showAssetBucketing={showAssetBucketing}
          availableAssetBuckets={availableAssetBuckets}
        />
      ) : (
        <>
          {actionSteps.length === 0 && (
            <Text pt="16">{isOther ? 'No Items' : 'No action steps'}</Text>
          )}
        </>
      )}

      <Box display="flex" flexDirection="column" css="gap: 16px">
        {actionSteps
          .sort((a, b) => {
            if (a.DueDate === b.DueDate) {
              return new Date(a.CreatedOn).getTime() - new Date(b.CreatedOn).getTime();
            }

            return new Date(a.DueDate ?? 0).getTime() - new Date(b.DueDate ?? 0).getTime();
          })
          .map(actionStep => (
            <TaskItem
              key={actionStep.Id}
              isReport
              isPrint={isPrint}
              {...actionStep}
              pulseCheckCategory={actionStep.PulseCheckCategory}
              advisorAssignees={advisorAssignees}
              showAssetBucketing={showAssetBucketing}
              availableAssetBuckets={availableAssetBuckets}
            />
          ))}
      </Box>
    </Box>
  );
};
